import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatNOK, formatDate, formatInvoiceType } from "@/lib/format";
import type { DBInvoice } from "@/lib/db-types";
import { effectiveInvoiceType } from "@/lib/invoice-plan";

const invoiceStatusColor: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-primary/10 text-primary",
  paid: "bg-success/10 text-success",
  overdue: "bg-destructive/10 text-destructive",
  credit_note: "bg-[#FFE1DB] text-[#7F1D1D]",
};

const invoiceStatusLabel: Record<string, string> = {
  draft: "Utkast",
  sent: "Sendt",
  paid: "Betalt",
  overdue: "Forfalt",
  credit_note: "Kreditnota",
};

/**
 * VIB-1290: fakturahistorikk på kundekortet. Fakturaer hentes i
 * use-customer-detail via kundens prosjekter OG kundens avtaler (dedup på id).
 */
export function CustomerInvoicesSection({ invoices }: { invoices: DBInvoice[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Fakturaer ({invoices.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen fakturaer for denne kunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Nr</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Type</th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Beløp</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">Dato</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-2 tabular-nums text-muted-foreground">
                      {inv.invoice_no ? `#${inv.invoice_no}` : "—"}
                    </td>
                    <td className="py-2 px-2">{formatInvoiceType(effectiveInvoiceType(inv), inv.poweroffice_status)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatNOK(inv.amount)}</td>
                    <td className="py-2 px-2">
                      <Badge
                        variant="secondary"
                        className={cn("text-xs", invoiceStatusColor[inv.status ?? "draft"])}
                      >
                        {invoiceStatusLabel[inv.status ?? "draft"] ?? inv.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-muted-foreground">
                      {formatDate(inv.invoice_date ?? inv.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
